import { createFileRoute, Link } from '@tanstack/react-router'

import { useDeleteSalesChannelGroup, useUpdateSalesChannelGroup } from '@/api/api-mutations'
import {
	preloadGetSalesChannelGroup,
	preloadListSalesChannelGroups,
	preloadListSalesChannels,
	useGetSalesChannelGroup,
	useListSalesChannelGroups,
	useListSalesChannels
} from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { toast } from '@/components/base/toast'
import { useAppForm } from '@/components/form/form'
import { SingleColumnPage } from '@/components/page-layout'
import { GenericDetailSkeleton } from '@/components/skeletons/generic-detail-skeleton'
import { useI18n } from '@/i18n/i18n-react'
import { Pencil } from '@/icons/pencil'
import { Trash } from '@/icons/trash'

import { GroupMembersTable } from '../../-group-members-table'

export const Route = createFileRoute('/_authenticated/settings/sales-channels/groups/$groupUuid')({
	component: RouteComponent,
	pendingComponent: GenericDetailSkeleton,
	// Remount when switching between groups so useAppForm reinitializes memberUuids.
	remountDeps: ({ params }) => params.groupUuid,
	loader: async ({ params: { groupUuid }, context }) => {
		await Promise.all([preloadListSalesChannels(context, {}), preloadListSalesChannelGroups(context, {})])
		return await preloadGetSalesChannelGroup(context, { path: { salesChannelGroupUuid: groupUuid } })
	},
	staticData: {
		crumb: ({ loaderData }) => loaderData?.name
	}
})

function RouteComponent() {
	const { groupUuid } = Route.useParams()
	const { L } = useI18n()
	const navigate = Route.useNavigate()
	const { data } = useGetSalesChannelGroup({ salesChannelGroupUuid: groupUuid })
	const salesChannels = useListSalesChannels()
	const groups = useListSalesChannelGroups()
	const updateGroup = useUpdateSalesChannelGroup()
	const deleteGroup = useDeleteSalesChannelGroup()
	const prompt = usePrompt()

	const form = useAppForm({
		defaultValues: {
			memberUuids: (data?.salesChannels.map(s => s.uuid) ?? []) as string[]
		},
		onSubmit: async ({ value }) => {
			const moving = (salesChannels.data ?? []).filter(
				sc => value.memberUuids.includes(sc.uuid) && sc.group && sc.group.uuid !== groupUuid
			)
			if (moving.length > 0) {
				const confirmed = await prompt({
					title: L.Settings.SalesChannels.Groups.Members.MoveWarning.Title(),
					description: L.Settings.SalesChannels.Groups.Members.MoveWarning.Description({
						count: moving.length,
						items: moving.map(sc => `${sc.name} (${sc.group!.name})`).join(', ')
					}),
					confirmText: L.General.Continue(),
					cancelText: L.General.Cancel()
				})
				if (!confirmed) {
					return
				}
			}
			const result = await updateGroup({
				path: { salesChannelGroupUuid: groupUuid },
				body: { memberUuids: value.memberUuids }
			})
			if (result.isSuccess) {
				toast.success(L.Settings.SalesChannels.Groups.Members.SaveSuccess())
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	const handleDelete = async () => {
		const res = await prompt({
			title: L.Settings.SalesChannels.Groups.Delete.Title(),
			description: L.Settings.SalesChannels.Groups.Delete.Description({ name: data?.name ?? '' }),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})
		if (!res) {
			return
		}
		const result = await deleteGroup({ path: { salesChannelGroupUuid: groupUuid } })
		if (result.isSuccess) {
			toast.success(L.Settings.SalesChannels.Groups.Delete.SuccessToast({ name: data?.name ?? '' }))
			navigate({ to: '/settings/sales-channels/groups', replace: true })
		} else {
			toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
		}
	}

	return (
		<SingleColumnPage hasOutlet>
			<Container>
				<ContainerHeader
					heading={data?.name ?? ''}
					rightSide={
						<Link from={Route.fullPath} to='edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<Pencil />
							</IconButton>
						</Link>
					}
					actions={[
						{
							actions: [
								{
									label: L.General.Delete(),
									icon: <Trash />,
									onClick: handleDelete
								}
							]
						}
					]}
				/>
			</Container>
			<form.AppForm>
				<Container className='flex flex-col'>
					<form.AppField
						name='memberUuids'
						children={field => (
							<GroupMembersTable
								title={L.Settings.SalesChannels.Groups.Members.Title()}
								items={salesChannels.data ?? []}
								groups={groups.data ?? []}
								value={field.state.value}
								onChange={field.handleChange}
								itemLink={uuid => ({
									to: '/settings/sales-channels/$salesChannelUuid',
									params: { salesChannelUuid: uuid }
								})}
								groupLink={uuid => ({
									to: '/settings/sales-channels/groups/$groupUuid',
									params: { groupUuid: uuid }
								})}
								labels={{
									searchPlaceholder: L.General.SearchPlaceholder(),
									advancedFilters: L.General.AdvancedFilters(),
									assignedColumn: L.Settings.SalesChannels.Groups.Members.Assigned(),
									nameColumn: L.Fields.Name(),
									groupColumn: L.Settings.SalesChannels.Column.Group(),
									unassignedLabel: L.Settings.SalesChannels.Groups.Members.FilterUnassigned(),
									empty: L.Settings.SalesChannels.Groups.Members.Empty()
								}}
							/>
						)}
					/>
				</Container>
				<div className='flex justify-end'>
					<form.SaveButton />
				</div>
			</form.AppForm>
		</SingleColumnPage>
	)
}
